import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    // target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateForMobileSlideshow, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Run filter on initial load (slideshow is hidden on desktop, shows on mobile)
    setTimeout(() => this.#updateMobileSlideshowVisibility(), 0);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Updates mobile slideshow visibility to show only the selected variant's media.
   * Replicates desktop grid behavior (variant-image-switcher) for the mobile slideshow.
   *
   * @param {VariantUpdateEvent} [event] - The variant update event (optional, for initial load uses form input).
   */
  #handleVariantUpdateForMobileSlideshow = (event) => {
    if (this.presentation !== 'grid') return;

    let variantId = event?.detail?.resource?.id;
    if (variantId == null) {
      const section = this.closest('.shopify-section, dialog');
      const variantInput = section?.querySelector('input[ref="variantId"]')
        ?? section?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      variantId = variantInput?.value;
    }
    if (!variantId) return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const slides = slideshow.refs.slides ?? this.querySelectorAll('slideshow-slide');
    const variantIdStr = String(variantId);

    for (const slide of slides) {
      const variantIds = slide.getAttribute('data-variant-id')?.split(',') ?? [];
      const isMatch = variantIds.length > 0 && variantIds.includes(variantIdStr);
      if (isMatch) {
        slide.removeAttribute('hidden');
      } else {
        slide.setAttribute('hidden', '');
      }
    }

    const featuredMediaId = event?.detail?.resource?.featured_media?.id;
    if (featuredMediaId) {
      slideshow.select({ id: String(featuredMediaId) }, undefined, { animate: false });
    } else {
      const visibleSlides = slideshow.slides ?? [];
      if (visibleSlides.length > 0) {
        slideshow.select(0, undefined, { animate: false });
      }
    }

    const visibleCount = (slideshow.slides ?? []).length;
    this.#updateSlideshowPaginationCount(visibleCount);
  };

  /**
   * Updates the slideshow pagination (dots/counter) to reflect the visible slide count.
   * @param {number} visibleCount - The number of visible slides.
   */
  #updateSlideshowPaginationCount(visibleCount) {
    if (!visibleCount) return;

    const controls = this.querySelectorAll('slideshow-controls');
    for (const control of controls) {
      const totalEl = control.querySelector('[ref="total"]');
      if (totalEl) {
        totalEl.textContent = String(visibleCount);
      }
      const dotsList = control.querySelector('ol.slideshow-controls__dots, ol[class*="dots"]');
      if (dotsList) {
        const items = dotsList.querySelectorAll('li');
        items.forEach((li, i) => {
          li.hidden = i >= visibleCount;
        });
      }
      const thumbnails = control.querySelectorAll('.slideshow-controls__thumbnail');
      thumbnails.forEach((btn, i) => {
        btn.hidden = i >= visibleCount;
      });
    }
  }

  /**
   * Runs the mobile slideshow visibility filter (for initial load).
   */
  #updateMobileSlideshowVisibility = () => {
    this.#handleVariantUpdateForMobileSlideshow(null);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
